import { useEffect, useState } from "react";

import styles from "../styles/GetAppLanding.module.scss";
// import coin from "../assets/coin.svg";
import inFlag from "../assets/india-flag.svg";
import offerIcon from "../assets/offer-icon.svg";
import paperPlane from "../assets/paper-plane.svg";
import sendArrow from "../assets/send-arrow.svg";
import usFlag from "../assets/us-flag.svg";
import { formatInr, formatRate, formatUsd, toNumber } from "./rateUtils";

const CurrencyBadge = ({ flag, label }) => (
  <span className={styles.currencyBadge}>
    <img src={flag} alt="" aria-hidden="true" />
    {label}
  </span>
);

const TransferBreakdown = ({ rateModel }) => {
  const promo = rateModel.promoBreakdown;
  const standard = rateModel.standardBreakdown;

  return (
    <div className={styles.transferBreakdown}>
      <div className={styles.breakdownRow}>
        <span>Transfer fee</span>
        <strong className={styles.freeTransferFee}>
          <span className={styles.strike}>$1.99</span>
          $0.00
        </strong>
      </div>
      {promo ? (
        <div className={styles.breakdownRow}>
          <span>
            Promo Rate <em>(first {formatUsd(promo.source_amount)})</em>
          </span>
          <strong>
            {formatUsd(promo.source_amount)} *{" "}
            {formatRate(promo.effective_fx_rate)}
          </strong>
        </div>
      ) : null}
      {standard ? (
        <div className={styles.breakdownRow}>
          <span>Standard Rate</span>
          <strong>
            {formatUsd(standard.source_amount)} *{" "}
            {formatRate(standard.effective_fx_rate)}
          </strong>
        </div>
      ) : null}
      <div className={`${styles.breakdownRow} ${styles.totalRow}`}>
        <span>Total</span>
        <strong>{formatInr(rateModel.recipientAmount, false)}</strong>
      </div>
    </div>
  );
};

const sanitizeAmount = (value) =>
  (value
    .replace(/,/g, "")
    .replace(/[^\d.]/g, "")
    .replace(/^(\d*\.?)|\./g, "$1")
    .match(/^\d*(?:\.\d{0,2})?/) || [""])[0];

const formatInputAmount = (value, locale = "en-US") => {
  const amount = toNumber(value);

  if (!amount) {
    return "";
  }

  return amount.toLocaleString(locale, {
    maximumFractionDigits: 2,
  });
};

const formatTypingAmount = (value, locale = "en-US") => {
  const sanitizedValue = sanitizeAmount(value);

  if (!sanitizedValue) {
    return "";
  }

  const [integerPart = "", decimalPart] = sanitizedValue.split(".");
  const formattedInteger = integerPart
    ? Number(integerPart).toLocaleString(locale)
    : "";

  if (sanitizedValue.includes(".")) {
    return `${formattedInteger || "0"}.${decimalPart || ""}`;
  }

  return formattedInteger;
};

const RemittanceCard = ({
  rateModel,
  onUsdAmountChange,
  onRecipientAmountChange,
}) => {
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [activeInput, setActiveInput] = useState(null);
  const [usdValue, setUsdValue] = useState(formatInputAmount(rateModel.amount));
  const [inrValue, setInrValue] = useState(
    formatInputAmount(rateModel.recipientAmount, "en-IN"),
  );

  useEffect(() => {
    if (activeInput !== "usd") {
      setUsdValue(formatInputAmount(rateModel.amount));
    }

    if (activeInput !== "inr") {
      setInrValue(formatInputAmount(rateModel.recipientAmount, "en-IN"));
    }
  }, [activeInput, rateModel.amount, rateModel.recipientAmount]);

  const handleUsdChange = (event) => {
    const value = sanitizeAmount(event.target.value);

    setActiveInput("usd");
    setUsdValue(formatTypingAmount(value));
    onUsdAmountChange(toNumber(value));
  };

  const handleInrChange = (event) => {
    const value = sanitizeAmount(event.target.value);

    setActiveInput("inr");
    setInrValue(formatTypingAmount(value, "en-IN"));
    onRecipientAmountChange(toNumber(value));
  };

  const handleAmountBlur = () => {
    setActiveInput(null);
  };

  const standardRateDisplay = formatRate(
    rateModel.standardBreakdown?.effective_fx_rate || rateModel.baseRate,
  );

  return (
    <div
      className={`${styles.phoneShell} ${detailsOpen ? styles.phoneShellExpanded : ""}`}
    >
      <div className={styles.liveRateBar}>
        <span>
          <i className={styles.liveRateDot} aria-hidden="true" />
          Live Rate
        </span>
        <strong>$1 = {standardRateDisplay}</strong>
      </div>
      <div className={styles.transferCard}>
        <div className={styles.amountGroup}>
          <span className={styles.inputLabel}>You send</span>
          <div className={styles.amountLine}>
            <div className={styles.amountInputWrap}>
              <span aria-hidden="true">$</span>
              <input
                aria-label="USD amount"
                inputMode="decimal"
                type="text"
                value={usdValue}
                onBlur={handleAmountBlur}
                onChange={handleUsdChange}
              />
            </div>
            <CurrencyBadge flag={usFlag} label="USD" />
          </div>
        </div>
        <div className={styles.amountGroup}>
          <span className={styles.inputLabel}>They receive</span>
          <div className={styles.amountLine}>
            <div className={styles.amountInputWrap}>
              <span aria-hidden="true">₹</span>
              <input
                aria-label="INR amount"
                inputMode="decimal"
                type="text"
                value={inrValue}
                onBlur={handleAmountBlur}
                onChange={handleInrChange}
              />
            </div>
            <CurrencyBadge flag={inFlag} label="INR" />
          </div>
        </div>
        <div className={styles.detailsHeader}>
          <button
            type="button"
            onClick={() => setDetailsOpen((value) => !value)}
          >
            Transfer details
            <span
              className={`${styles.detailsChevron} ${
                detailsOpen ? styles.chevronOpen : ""
              }`}
              aria-hidden="true"
            />
          </button>
          <span className={styles.bestRate}>Best FX rate applied</span>
        </div>
        <div
          className={`${styles.transferBreakdownWrap} ${
            detailsOpen ? styles.transferBreakdownOpen : ""
          }`}
          aria-hidden={!detailsOpen}
        >
          <TransferBreakdown rateModel={rateModel} />
        </div>
        <div className={styles.offerCard}>
          <div>
            <h3>
              <span>{rateModel.displayPromoRate}</span> Promo Rate. $0 Fees
            </h3>
            <p>Applicable on your first transfer</p>
            <span className={styles.offerChip}>
              <img src={offerIcon} alt="" aria-hidden="true" />
              Limited Time Offer
            </span>
          </div>
          <div className={styles.offerArt}>
            <img
              className={styles.plane}
              src={paperPlane}
              alt=""
              aria-hidden="true"
            />
            {/* <img className={styles.coin} src={coin} alt="" aria-hidden="true" /> */}
          </div>
        </div>
        <a
          className={styles.sendButton}
          href="https://accounts.joinabound.com/getapp/signup/v2?source=remittance"
        >
          Send to India
          <img src={sendArrow} alt="" aria-hidden="true" />
        </a>
      </div>
    </div>
  );
};

export default RemittanceCard;
